/**
 * Sentence shown under "What happened:" in place of tool calls a model wrote as text (MEDA-780).
 * Worded like the observation the worker now writes itself (`_TEXT_TOOL_CALLS_OBSERVATION` in
 * cti-quality-worker), so results stored before and after that fix read the same way.
 */
export const TEXT_TOOL_CALLS_EXPLANATION =
  'The model wrote its tool calls as text instead of calling the tools, so nothing was executed and the case has no verdict.';

const TEXT_TOOL_CALL_BLOCK =
  /```[a-zA-Z]*\s*([\s\S]*?)```|<tool_call>([\s\S]*?)<\/tool_call>/g;
const TOOL_NAME_KEYS = ['tool', 'name', 'tool_name', 'function'];
const TOOL_ARGUMENT_KEYS = ['arguments', 'parameters', 'args', 'input'];

/** True when one parsed JSON value is a tool name paired with an arguments object. */
function isToolCallShape(candidate: unknown): boolean {
  if (Array.isArray(candidate))
    return candidate.length > 0 && candidate.every(isToolCallShape);
  if (typeof candidate !== 'object' || candidate === null) return false;
  const fields = candidate as Record<string, unknown>;
  const hasName = TOOL_NAME_KEYS.some((key) => typeof fields[key] === 'string');
  const hasArguments = TOOL_ARGUMENT_KEYS.some(
    (key) =>
      typeof fields[key] === 'object' &&
      fields[key] !== null &&
      !Array.isArray(fields[key]),
  );
  return hasName && hasArguments;
}

/** Parse one text block as JSON, or undefined when it is not JSON. */
function parseJson(block: string): unknown {
  try {
    return JSON.parse(block.trim());
  } catch {
    return undefined;
  }
}

/** True when a text block is one tool call, an array of them, or consecutive tool-call objects. */
function isToolCallText(block: string): boolean {
  return (
    isToolCallShape(parseJson(block)) ||
    isToolCallShape(parseJson(`[${block.replace(/}\s*\n\s*{/g, '},{')}]`))
  );
}

/**
 * True for a step observation that holds tool calls a model wrote out as JSON (a fenced block, a
 * `<tool_call>` tag or a bare object) instead of calling the tools. The worker copies the model's
 * final text into a synthesized step when no step result was reported, so such text reached the
 * report raw. Each block, and unwrapped text, is also read as consecutive objects (`{...}\n{...}`),
 * the shape several calls take together. Blocks count only when every block is a tool call and
 * nothing but whitespace surrounds them, so real evidence next to a block is never hidden. The shape
 * rules follow `wrote_tool_calls_as_text` in cti-quality-worker's Pi adapter; this check is stricter
 * about surrounding text because, unlike the worker, it cannot see whether any real call ran.
 */
export function isTextToolCallObservation(observation?: unknown): boolean {
  if (typeof observation !== 'string' || !observation.trim()) return false;
  const blocks = [...observation.matchAll(TEXT_TOOL_CALL_BLOCK)].map(
    (match) => match[1] ?? match[2] ?? '',
  );
  if (blocks.length === 0) return isToolCallText(observation);
  if (observation.replace(TEXT_TOOL_CALL_BLOCK, '').trim()) return false;
  return blocks.every(isToolCallText);
}
